import { cn, Skeleton } from '@today/ui-primitives'
import type { ReactElement } from 'react'

/**
 * Built-in strings of {@link ListSkeleton}, overridable per locale (tech §59).
 *
 * @property loading - Accessible name of the status container. Default `Loading`.
 */
export interface ListSkeletonLabels {
	loading: string
}

const DEFAULT_LABELS: ListSkeletonLabels = { loading: 'Loading' }

/**
 * Props for the {@link ListSkeleton} molecule.
 *
 * @property rows - How many placeholder rows to render. Defaults to 3.
 * @property className - Extra Tailwind classes merged onto the container.
 * @property labels - Localised built-in strings; each omitted key falls back to English.
 *
 * @example
 * ```tsx
 * {isLoading ? <ListSkeleton rows={5} /> : <Table {...tableProps} />}
 * ```
 */
export interface ListSkeletonProps {
	rows?: number
	className?: string
	labels?: Partial<ListSkeletonLabels>
}

/** Placeholder rows to render when the consumer supplies no `rows`. */
const DEFAULT_ROWS = 3

/**
 * Loading placeholder standing in for a list or table while data is in flight.
 *
 * @remarks
 * - **Accessibility:** one `role="status"` container named `labels.loading` (default `Loading`) announces
 *   the wait; the placeholder rows themselves are `aria-hidden`, so a screen
 *   reader hears the status once rather than reading decorative bars.
 * - **Composition:** each row is the primitives-layer `<Skeleton>`.
 * - **Presentational only:** it knows nothing about what is loading.
 * - **Styling:** every spacing class is a `ds-*` token utility.
 *
 * @see packages/ui-components/src/list-skeleton/list-skeleton.stories.tsx for visual examples
 */
export function ListSkeleton({ rows = DEFAULT_ROWS, className, labels }: ListSkeletonProps): ReactElement {
	const text = { ...DEFAULT_LABELS, ...labels }
	return (
		<div role="status" aria-label={text.loading} className={cn('flex flex-col gap-ds-2', className)}>
			{Array.from({ length: rows }, (_, index) => (
				// Placeholders are interchangeable and never reordered, so the index is a
				// stable key here.
				// biome-ignore lint/suspicious/noArrayIndexKey: decorative, fixed-length placeholder rows.
				<div key={index} data-testid="list-skeleton-row" aria-hidden="true">
					<Skeleton className="h-ds-10 w-full" />
				</div>
			))}
		</div>
	)
}
